import { ColumnDef, ColumnHelper } from '@tanstack/react-table';
import { INSTAGRAM_CONTENT_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import { ISearchContentResult } from '~/searchApi/content';
import { formatAbbr, formatPercentage } from '~/utils';

import {
  COLUMNS,
  COLUMN_METHOD,
  DEFAULT_COLUMNS,
  NOT_APPLICABLE,
  PLATFORM_COLUMNS,
} from './SearchContentTable.constants';

export function getEngagements(content: ISearchContentResult) {
  if (
    content.platform === SOCIAL_PLATFORMS.instagram &&
    content.channel === INSTAGRAM_CONTENT_TYPES.reels
  ) {
    return formatAbbr(content.interactions);
  }
  if (
    content.platform === SOCIAL_PLATFORMS.tiktok ||
    (content.platform === SOCIAL_PLATFORMS.instagram &&
      content.channel === INSTAGRAM_CONTENT_TYPES.reels)
  ) {
    return formatAbbr(content.engagements);
  }

  return NOT_APPLICABLE;
}

export function getReachEngRate(content: ISearchContentResult) {
  return content.platform === SOCIAL_PLATFORMS.instagram
    ? formatPercentage(content.postReachEngRate, NOT_APPLICABLE)
    : NOT_APPLICABLE;
}

export function getViewsEngRate(content: ISearchContentResult) {
  if (
    content.platform === SOCIAL_PLATFORMS.instagram &&
    content.channel === INSTAGRAM_CONTENT_TYPES.reels
  ) {
    return formatPercentage(content.viewsEngRate, NOT_APPLICABLE);
  }
  return NOT_APPLICABLE;
}

export function getViews(content: ISearchContentResult) {
  if (
    content.platform === SOCIAL_PLATFORMS.instagram &&
    content.channel === INSTAGRAM_CONTENT_TYPES.reels
  ) {
    return formatAbbr(
      content.viewsCount || content.igReelsAggregatedAllPlaysCount,
      NOT_APPLICABLE,
    );
  }
  if (
    content.platform === SOCIAL_PLATFORMS.instagram &&
    (content.channel === INSTAGRAM_CONTENT_TYPES.post ||
      content.channel === INSTAGRAM_CONTENT_TYPES.story)
  ) {
    return formatAbbr(
      content.viewsCount || content.impressions,
      NOT_APPLICABLE,
    );
  }

  return formatAbbr(content.views, NOT_APPLICABLE);
}

export function getAverageViewPercentage(content: ISearchContentResult) {
  return content.platform === SOCIAL_PLATFORMS.youtube
    ? formatPercentage(content.viewRate, NOT_APPLICABLE)
    : NOT_APPLICABLE;
}

export function getShares(content: ISearchContentResult) {
  if (
    content.platform === SOCIAL_PLATFORMS.instagram &&
    content.channel === INSTAGRAM_CONTENT_TYPES.story
  ) {
    return formatAbbr(content.shares);
  }

  return NOT_APPLICABLE;
}

export function getReplies(content: ISearchContentResult) {
  if (
    content.platform === SOCIAL_PLATFORMS.instagram &&
    content.channel === INSTAGRAM_CONTENT_TYPES.story
  ) {
    return formatAbbr(content.replies);
  }

  return NOT_APPLICABLE;
}

interface IGetColumnsData {
  platforms?: string[];
  query?: string;
}

export const getColumns = (
  columnHelper: ColumnHelper<ISearchContentResult>,
  publishedAtColumnEnabled = false,
  { platforms, query }: IGetColumnsData,
): ColumnDef<ISearchContentResult, string>[] => {
  const columnsEnabled = platforms?.length
    ? platforms.map((platform: SOCIAL_PLATFORMS) => {
        return PLATFORM_COLUMNS[platform];
      })
    : DEFAULT_COLUMNS;

  // If we have more than one platform,
  // the columns may be duplicated.
  // We should remove duplicates to calculate
  // the columns
  const uniqueEnabledColumnValues = Array.from(
    new Set([
      ...(publishedAtColumnEnabled ? [COLUMNS.publishedAt] : []),
      ...columnsEnabled.flat(),
    ]),
  );

  const columns = uniqueEnabledColumnValues.map((key) =>
    COLUMN_METHOD[key](columnHelper, { platforms, query }),
  );

  return columns;
};
